import { ReactElement, useEffect, useMemo, useState } from "react";

import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/_ui/command";

import { useDatatableContext } from "../use-datatable-context";

import {
  displayTextForFilterMode,
  FilterByNumberMode,
  FilterByNumberPayload,
  FilterByNumberPayloadSchema,
} from "./functions/filter-by-number";

export interface FilterByNumberItem {
  value: number;
  mode: FilterByNumberMode;
}

export const renderFilterByNumberItem = <TItem extends FilterByNumberItem>(
  item: TItem,
  view: "filter-mode" | "value" | "filter-mode-value" = "filter-mode-value",
) => {
  switch (view) {
    case "filter-mode":
      return <span className="whitespace-nowrap">{displayTextForFilterMode(item.mode)}</span>;
    case "value":
      return <span className="font-semibold">{item.value}</span>;
    case "filter-mode-value":
      return (
        <div className="flex flex-row items-center gap-1">
          {displayTextForFilterMode(item.mode)}
          <span className="font-semibold">{item.value}</span>
        </div>
      );
  }
};

export interface FilterByNumberConfigurationViewProps<TItem extends FilterByNumberItem> {
  column: {
    id: string;
  };
  onFinished: () => void;
  defaultValue?: TItem;
  allowedFilterModes?: FilterByNumberMode[];
  renderItem?: (item: TItem, view: "filter-mode" | "value" | "filter-mode-value") => ReactElement;
}

export const FilterByNumberConfigurationView = <TItem extends FilterByNumberItem>({
  onFinished,
  defaultValue,
  renderItem = renderFilterByNumberItem<TItem>,
  allowedFilterModes = ["less_than", "more_than", "equals", "not_equals"],
  ...props
}: FilterByNumberConfigurationViewProps<TItem>) => {
  const [query, setQuery] = useState<string>(defaultValue ? String(defaultValue.value) : "");
  const { table, state, getConfigurationForColumn } = useDatatableContext();
  const { columnFilters } = state;

  const column = table.getColumn(props.column.id);
  const columnConfiguration = getConfigurationForColumn(props.column.id);

  const [selectedFilterByNumberItem, setSelectedFilterByNumberItem] = useState<FilterByNumberItem | null>(
    defaultValue ?? null,
  );

  const parsedQuery = useMemo(() => {
    const parsed = parseFloat(query);
    return isNaN(parsed) ? null : parsed;
  }, [query]);

  const filterValue = useMemo(() => {
    const filterValue = column?.getFilterValue();

    const safePayload = FilterByNumberPayloadSchema.safeParse(filterValue);
    if (!safePayload.success) {
      return null;
    }

    return safePayload.data;

    // We also listen to the columnFilters to update the filter value when the user changes the filter mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, columnFilters]);

  useEffect(() => {
    if (!filterValue) {
      return;
    }
    setSelectedFilterByNumberItem({
      value: filterValue.query,
      mode: filterValue.mode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedFilterByNumberItem) {
      return;
    }
    if (selectedFilterByNumberItem.value === null || selectedFilterByNumberItem.mode === undefined) {
      clearFilter();
      return;
    }

    setFilter({
      type: "filter_by_number",
      mode: selectedFilterByNumberItem.mode,
      query: selectedFilterByNumberItem.value,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterByNumberItem]);

  const setFilter = (payload: FilterByNumberPayload) => {
    column?.setFilterValue(payload);
  };

  const clearFilter = () => {
    if (!column) {
      return;
    }
    table.setColumnFilters(columnFilters.slice().filter((filterValue) => filterValue.id !== column.id));
  };

  const handleSelectFilterByNumber = (item: FilterByNumberItem, shouldFinish?: boolean) => {
    setSelectedFilterByNumberItem(item);

    if (shouldFinish) {
      setTimeout(function () {
        onFinished();
      }, 1);
    }
  };

  return (
    <Command data-testid="filter-by-number-configuration-view">
      <CommandInput
        placeholder={columnConfiguration?.getDisplayValue() ?? "Filtern ..."}
        value={query}
        onValueChange={setQuery}
        autoFocus={true}
        containerClassName={parsedQuery !== null ? "border-b" : "border-none"}
      />
      {parsedQuery !== null && (
        <CommandList>
          <CommandGroup>
            {allowedFilterModes.map((filterMode) => {
              return (
                <CommandItem
                  key={filterMode}
                  value={query}
                  className="group flex flex-row items-center gap-1 p-1 py-1.5 hover:cursor-pointer aria-selected:bg-background aria-selected:text-foreground aria-selected:hover:bg-slate-100 aria-selected:hover:text-slate-900 dark:aria-selected:bg-background dark:aria-selected:text-foreground dark:aria-selected:hover:bg-slate-800 dark:aria-selected:hover:text-slate-50"
                  onSelect={() => {
                    handleSelectFilterByNumber({ value: parsedQuery, mode: filterMode }, true);
                  }}
                >
                  {renderItem({ value: parsedQuery, mode: filterMode } as TItem, "filter-mode-value")}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      )}
    </Command>
  );
};
